import { useState } from 'react'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Trash2, Edit, Check, MapPin } from 'lucide-react'
import type { Address } from '@/types/user'

export default function AccountAddressesPage() {
  const { user, addresses, addAddress, updateAddress, deleteAddress, setDefaultAddress } = useAuthStore()
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formData, setFormData] = useState<Omit<Address, 'id' | 'created_at'>>({
    user_id: user?.id || '',
    label: '',
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: 'Dhaka',
    area: '',
    postal_code: '',
    is_default: false,
  })

  const resetForm = () => {
    setFormData({
      user_id: user?.id || '',
      label: '',
      full_name: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: 'Dhaka',
      area: '',
      postal_code: '',
      is_default: false,
    })
    setIsAdding(false)
    setEditingId(null)
  }

  const handleSubmit = () => {
    if (editingId) {
      updateAddress(editingId, formData)
    } else {
      addAddress(formData)
    }
    resetForm()
  }

  const handleEdit = (address: Address) => {
    setFormData(address)
    setEditingId(address.id)
    setIsAdding(true)
  }

  if (!user) return null

  return (
    <div className="space-y-6">
      {/* Add Address Button */}
      {!isAdding && (
        <Button onClick={() => setIsAdding(true)} className="w-full md:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add New Address
        </Button>
      )}

      {/* Add/Edit Address Form */}
      {isAdding && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h2 className="text-xl font-bold text-foreground mb-4">
            {editingId ? 'Edit Address' : 'Add New Address'}
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Label <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g., Home, Office"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Full Name <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="Recipient's name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="+880 17XXXXXXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                City <span className="text-destructive">*</span>
              </label>
              <select
                className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              >
                <option value="Dhaka">Dhaka</option>
                <option value="Chittagong">Chittagong</option>
                <option value="Sylhet">Sylhet</option>
                <option value="Rajshahi">Rajshahi</option>
                <option value="Khulna">Khulna</option>
                <option value="Barisal">Barisal</option>
                <option value="Rangpur">Rangpur</option>
                <option value="Mymensingh">Mymensingh</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Area <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="e.g., Gulshan, Dhanmondi"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Postal Code
              </label>
              <Input
                placeholder="e.g., 1212"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Address Line 1 <span className="text-destructive">*</span>
              </label>
              <Input
                placeholder="House/Flat number, Road number"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Address Line 2
              </label>
              <Input
                placeholder="Nearby landmark (optional)"
                value={formData.address_line2 || ''}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="rounded border-input"
                />
                <span className="text-sm font-medium text-foreground">
                  Set as default address
                </span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button onClick={handleSubmit}>
              {editingId ? 'Update Address' : 'Save Address'}
            </Button>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Address List */}
      {addresses.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No addresses saved yet
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Add your delivery addresses for faster checkout
          </p>
          <Button onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Address
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`bg-card rounded-lg border ${
                address.is_default ? 'border-primary' : 'border-border'
              } p-6 relative`}
            >
              {address.is_default && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  <Check className="h-3 w-3" />
                  Default
                </span>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  {address.label}
                </h3>
                <p className="text-sm font-medium text-foreground">{address.full_name}</p>
                <p className="text-sm text-muted-foreground">{address.phone}</p>
              </div>

              <div className="text-sm text-muted-foreground mb-4">
                <p>{address.address_line1}</p>
                {address.address_line2 && <p>{address.address_line2}</p>}
                <p>{address.area}, {address.city} {address.postal_code}</p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(address)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                {!address.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDefaultAddress(address.id)}
                  >
                    Set as Default
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deleteAddress(address.id)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
